/**
 * App-side wiring for the Work-home surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move: the landing's desktop-target picker is what enables the roster
 * query, the Library thumbnail's signed-URL read is scoped to a mounted card,
 * and the default-landing banner's preference read is gated on the rest of its
 * chain — resolving any of them here would move a query to a render that does
 * not happen today.
 *
 * IMPORTED FOR EFFECT BY `src/components/layout/scribe-layout.tsx`, not by the
 * Work route. The route is a Chat file, and an `@/utils` import there raises the
 * Chat ledger's zone count, which is a ruling rather than a migration. The
 * layout is the route's own static import and is evaluated before it renders.
 */
import { EvidenceRegulatoryFootnoteWithIfu } from '@/app-shell/settings/account/components/evidence-regulatory-footnote-with-ifu';
import { useEvidenceIfuHref } from '@/app-shell/settings/account/components/useIsEvidenceIfuVisible';
import { DEFAULT_LANDING_PAGE_SETTINGS_HASH } from '@/app-shell/settings/display-controls/components/default-landing-page/landing-page-options';
import { useQueryDefaultLandingPage } from '@/app-shell/settings/display-controls/components/default-landing-page/useDefaultLandingPageSetting';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { agentRecordingSessionIdAtom } from '@/atoms/agentRecordingAtoms';
import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import {
  hasBootLandedOnHomeAtom,
  isHomeBootPath,
  readBootPathname,
} from '@/atoms/bootLandingAtoms';
import { evidenceDisclaimerModalOpenAtom } from '@/atoms/evidenceProModalAtoms';
import {
  formDraftTitleAtom,
  formFillingUploadModalOpenAtom,
  formTemplateToEditAtom,
} from '@/atoms/formFillingAtom';
import { pendingLettersAtomFamily } from '@/atoms/letterAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import {
  ROUTINES_DESKTOP_NURTURE_BANNER_COUNT_THRESHOLD,
  routinesDesktopNurtureBannerDismissedAtom,
} from '@/atoms/routinesDesktopNurtureAtom';
import { sessionWindowsAtomFamily } from '@/atoms/sessionWindowsAtoms';
import {
  presentationAtom,
  presentationHistoryAtom,
  presentationOriginalAtom,
  selectedSlideIndexAtom,
} from '@/atoms/slideBuilderAtoms';
import { DEFAULT_DATE_FORMAT } from '@/components/constants';
import { cancelNavMenuSwitchTracking } from '@/components/layout/Navigation/sidebar/nav-menu-switch-tracking';
import { isShortcutsDisabledAtom } from '@/components/layout/ShortcutsHelper/shortcutsAtom';
import { mobileAppHeaderMessages } from '@/components/layout/header/mobile-app-header/mobile-app-header.messages';
import { DesktopBars } from '@/components/mic-selector/DesktopBars';
import { useSaveFormTemplate } from '@/components/modal/form-filling-modals/view-form-modal/useSaveFormTemplate';
import { FormViewType } from '@/components/modal/form-filling-modals/view-form-modal/view-form-modal.types';
import { OverlayShimmerText } from '@/components/overlay/overlay-shimmer-text';
import { usePresentation } from '@/components/slide-builder/hooks/usePresentation';
import {
  handleSlideDeleteChunk,
  handleSlideUpdateChunk,
} from '@/components/slide-builder/hooks/useSlideUpdateListener';
import { slideBuilderMessages } from '@/components/slide-builder/slide-builder.messages';
import {
  useSlideToolbarActions,
  useSlideUndoRedoHotkeys,
} from '@/components/slide-builder/useSlideToolbarActions';
import {
  BoldButton,
  BulletListButton,
  ItalicButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { mergeCreditAllowanceGhost } from '@/components/ui/agent-credit-allowance-banner/mergeCreditAllowanceGhost';
import { ImageFileCard } from '@/components/ui/attachments/ImageFileCard';
import { ComposerDictateSlot } from '@/components/ui/composer-dictate-slot';
import { renderPlaceholderSlots } from '@/components/ui/composer-placeholder-slots';
import { getResolvedShortcutsForMarket } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import {
  buildSearchableShortcuts,
  filterShortcuts,
} from '@/components/ui/floating-ask-heidi/shortcut-search';
import { anyDialogDrawerOpenAtom } from '@/components/ui/mobile-responsive-dialog-drawer/atoms';
import { useComposerShortcutsFlyout } from '@/components/ui/session-ask-heidi/use-in-session-shortcut-flyout';
import {
  usePersistedWorkMode,
  useStartEvidenceChat,
} from '@/components/ui/session-ask-heidi/use-persisted-work-mode';
import { DraftNoteBody } from '@/components/ui/work-tool-cards/DraftNoteBody';
import { OpenTemplateEditorBlock } from '@/components/ui/work-tool-cards/OpenTemplateEditorBlock';
import { TemplatePinBlock } from '@/components/ui/work-tool-cards/TemplatePinBlock';
import { WorkArtefactCreatedCard } from '@/components/ui/work-tool-cards/WorkArtefactCreatedCard';
import { invalidateShellCachesOnEvent } from '@/composition/hosts/agents/agent-events/invalidate-agent-event-shell-caches';
import {
  candidateMappingKey,
  candidateMappingsAtom,
  clearCandidateMappingsAtom,
  ingestFieldCandidatesAtom,
  ingestNotePushHintsAtom,
  ingestNotePushRecordedAtom,
  setSectionOverrideAtom,
} from '@/composition/hosts/agents/computer-use-note-push/candidate-mappings';
import { detectShortcutConflict } from '@/composition/hosts/agents/routines/workflow-shortcut-conflict';
import { ChatObjectTokenHoverCard } from '@/composition/hosts/chat-object-hover-card';
import {
  ChatObjectReference,
  RefMarkdownElement,
} from '@/composition/hosts/chat-object-reference';
import { useCachedReferenceLabel } from '@/composition/hosts/chat-object-reference-cache';
import { SessionReferenceOpenContext } from '@/composition/hosts/chat-object-reference-kinds';
import { patientSexAge } from '@/composition/hosts/chat-object-reference-label';
import { EvidenceAnswerPortsHost } from '@/composition/hosts/evidence-answer-ports-host';
import { useLetterSenderActions } from '@/composition/hosts/integrations/letter-sender-v2/hooks/useLetterSenderActions';
import { useOpenPatientFullPage } from '@/composition/hosts/patients/patient-profile/useOpenPatientFullPage';
import { usePatientProfileHost } from '@/composition/hosts/patients/patient-profile/usePatientProfileHost';
import { handleEditTemplateHost } from '@/composition/hosts/templates/template-builder/handle-edit-template-host';
import { useTemplateCreationApprovalGuard } from '@/composition/hosts/templates/template-builder/useTemplateCreationApprovalGuard';
import { useEvidenceSourceOpeners } from '@/composition/hosts/use-evidence-answer-ports';
import { GET_SESSIONS_LIST } from '@/constants/sessions';
import { TabType } from '@/container/ScribeTabs/index.v2.types';
import {
  focusedArtifactTab,
  toFocusedArtifactTarget,
} from '@/container/session-page/v2/main/focused-artifact-target';
import {
  loadSplitScreen,
  reportSplitScreenPreloadFailure,
} from '@/container/session-panel/split-screen-loader';
import { ChatMessageContext } from '@/context/ChatMessageContext';
import { useWhiteLabelSafe } from '@/context/white-label';
import { copilotRunContextByProjectAtom } from '@/features/copilot/copilot-run-sessions';
import { useDictationStore } from '@/features/dictation/dictationBubble/store/useDictationStore';
import {
  AGENT_HOME_V1_FLAG_KEY,
  useIsAgentHomeV1Enabled,
  useIsAskHeidiSourcesShortcutsEnabled,
  useIsChatObjectRefsEnabled,
  useIsCmdShiftHOverlayEnabled,
  useIsCountrySpecificEhrSelectionEnabled,
  useIsDefaultLandingPageSettingEnabled,
  useIsEvidenceInlineImagesEnabled,
  useIsEvidenceSubagentResidualCapabilitiesEnabled,
  useIsNangoIntegrationsEnabled,
  useIsPatientListEnabled,
  useIsProjectsContextualLoaderPhrasesEnabled,
  useIsProjectsEnabled,
  useIsRpaRecorderEnabled,
  useIsWorkEvidenceDisclaimerEnabled,
  useIsWorkflowsEnabled,
  useIsWorkInSessionEnabled,
  useIsWorkLetterArtifactEnabled,
  useIsWorkSessionArtifactV2Enabled,
} from '@/features/flags';
import { useDefaultLandingPageBannerWaitMs } from '@/features/flags/default-landing-page-banner-wait';
import { useIsDesktopTargetSelectorEnabled } from '@/features/flags/desktop-target-selector-flag';
import { useFormFieldEditorFlags } from '@/features/flags/form-field-editor-flags';
import {
  IS_CHAT_ATTACHMENTS_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_SLIDE_BUILDER_ENABLED,
} from '@/features/flags/legacy-flags';
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY } from '@/features/flags/work-home-object-workspace-flag';
import { useIsWorkSourceCitationChipsEnabled } from '@/features/flags/work-source-citation-chips-flag';
import { personalizationSharedMessages } from '@/features/onboarding-v2/components/personalization/shared/personalization-shared.messages';
import {
  buildCitationRegistry,
  buildImageRegistry,
  parseEvidenceAgentSearchPayload,
  toChatSource,
} from '@/features/projects/components/evidence-subagent/citation-tokens/citation-model';
import {
  buildCiteMarkers,
  buildImageMarkers,
  buildReferences,
  stripCiteTags,
  stripImgTags,
} from '@/features/projects/components/evidence-subagent/citation-tokens/resolve';
import { useEvidenceAnswerComponents } from '@/features/projects/components/evidence-subagent/hooks/useEvidenceAnswerComponents';
import { useProjectEvidenceSession } from '@/features/projects/hooks/useProjectEvidenceSession';
import {
  buildEvidenceSessionFromPayload,
  readEvidenceRenderPayload,
} from '@/features/projects/state/evidence-render-payload';
import {
  evidenceSessionsAtom,
  handleEvidenceChunkStreamEvent,
} from '@/features/projects/state/evidence-sessions';
import { renderCreateTemplateToolUse } from '@/features/projects/timeline/evidence-tool-adapters/create-template';
import { renderUpdateWordLibraryToolUse } from '@/features/projects/timeline/evidence-tool-adapters/update-word-library';
import { getAuthToken } from '@/features/rpa/agent-client/auth-token';
import { cancelComputerUseRun } from '@/features/rpa/agent-client/cancel-computer-use-run';
import { useProjectFileExecutorAttach } from '@/features/rpa/agent-client/useProjectFileExecutorAttach';
import {
  classifyCuaCloudProviderState,
  CUA_CLOUD_MACHINE_QUERY_KEY,
  cuaCloudClient,
} from '@/features/rpa/cua-cloud/cuaCloud';
import { desktopControlApprovalPort } from '@/features/rpa/desktop-control-approval';
import {
  releaseDesktopControlApprovalReservation,
  releaseDesktopControlApprovalReservationsForAgentRun,
} from '@/features/rpa/desktop-control-approval-reservation';
import { subscribeDesktopControlLease } from '@/features/rpa/desktop-control-lease';
import { useDesktopPermissionGate } from '@/features/rpa/desktop-permission-gate';
import { isValidDesktopInstanceId } from '@/features/rpa/desktop-target/desktopInstanceId';
import { isThisComputerTarget } from '@/features/rpa/desktop-target/desktopTargetSelection';
import { DESKTOP_TARGETS_QUERY_KEY } from '@/features/rpa/desktop-target/desktopTargets';
import {
  readNativeInstanceId,
  useNativeInstanceId,
} from '@/features/rpa/desktop-target/nativeInstanceId';
import { resolveDesktopTargetTurn } from '@/features/rpa/desktop-target/resolveDesktopTargetTurn';
import { useComputerUseQueuedTarget } from '@/features/rpa/desktop-target/useComputerUseQueuedTarget';
import { useDesktopTargetRoster } from '@/features/rpa/desktop-target/useDesktopTargetRoster';
import { useDesktopTargetSelection } from '@/features/rpa/desktop-target/useDesktopTargetSelection';
import { useExecutorStopSignal } from '@/features/rpa/executor/useExecutorStopSignal';
import {
  emitProjectAgentRegistryPoke,
  listenProjectAgentRegistryPoke,
} from '@/features/rpa/project-agent-ipc';
import { ProtectedActionApproval } from '@/features/rpa/protected-action-approval';
import {
  ParamChip,
  TemplateParts,
} from '@/features/rpa/recorder/components/workflow/workflow-step-template';
import { useUserWorkflows } from '@/features/rpa/recorder/hooks/useUserWorkflows';
import { deriveAvailableWorkflowSkillsFromSummaries } from '@/features/rpa/recorder/lib/derive-available-workflow-skills-from-summaries';
import { deriveWorkflowDetailPresentation } from '@/features/rpa/recorder/lib/derive-workflow-detail-presentation';
import { humanizeParameterName } from '@/features/rpa/recorder/lib/humanize-parameter-name';
import { parseWorkflow } from '@/features/rpa/recorder/lib/parse-workflow';
import { useOpenRoutineCreateChat } from '@/features/workflows/hooks/useOpenRoutineCreateChat';
import { useRecentRunsRail } from '@/features/workflows/hooks/useRecentRunsRail';
import { useRunRoutineToChat } from '@/features/workflows/hooks/useRunRoutineToChat';
import { writeHtmlAndMarkdownToClipboard } from '@/helpers/copyHelpers/copyHelpers-v2';
import { describeClipboardError } from '@/helpers/copyHelpers/describeClipboardError';
import { getEhrProviderMetadata } from '@/helpers/ehrProviderMetadata';
import { getDomains } from '@/helpers/getDomains';
import { getRegion } from '@/helpers/getRegion';
import { useAudioLevelsSubscription } from '@/hooks/desktop/useAudioLevelsSubscription';
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { useIsEntitlementEnabled } from '@/hooks/entitlements/useIsEntitlementEnabled';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useMutateUpdatePatientProfile } from '@/hooks/mutation/useMutateUpdatePatientProfile';
import { useMutateUpdateSessionRecordingStatus } from '@/hooks/mutation/useMutateUpdateSessionRecordingStatus';
import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import { useOverlayProjectCreatedListener } from '@/hooks/overlay/useOverlayProjectCreatedListener';
import { useQueryEvidenceIntegrations } from '@/hooks/query/integrations/evidence/useQueryEvidenceIntegrations';
import { NOTE_PUSH_TARGETS_QUERY_KEY } from '@/hooks/query/note-push-targets-query-key';
import { useQueryComputerUseNotePushTargets } from '@/hooks/query/useQueryComputerUseNotePushTargets';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryDocumentsBySessionId } from '@/hooks/query/useQueryDocumentsBySessionId';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import {
  useQuerySessionWindows,
  useSessionWindow,
  useSessionWindows,
} from '@/hooks/query/useQuerySessionWindows';
import { useQuerySessionsV2 } from '@/hooks/query/useQuerySessionsV2';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useUserCountryCode } from '@/hooks/query/useUserCountryCode';
import { getPreviewUrl, useChatFileUpload } from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { useChatPaste } from '@/hooks/useChatPaste';
import { SessionIdOverrideContext, useCurrentSessionId } from '@/hooks/useCurrentIds';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceChatSurfaceGate } from '@/hooks/useEvidenceChatSurfaceGate';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import { useFormDraftTitleState } from '@/hooks/useFormDraftTitleState';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { useIsWorkDesktopNurtureBannerEnabled } from '@/hooks/useIsWorkDesktopNurtureBannerEnabled';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOpenSessionPage } from '@/hooks/useOpenSessionPage';
import { useRecording } from '@/hooks/useRecording/useRecording';
import {
  useRecordingControlCommands,
  type RecordingControlResult,
} from '@/hooks/useRecording/useRecordingControlCommands';
import {
  usePendingWorkFirstTurnHandoff,
  useSessionWorkLandingResearch,
  useSessionWorkLandingViewModel,
} from '@/hooks/useSessionWorkLandingViewModel';
import { getPlatform } from '@/lib/analytics/legacy-sender-context';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { track } from '@/lib/analytics/track';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { cn } from '@/lib/cn';
import { FORM_FIELD_EDITOR_PORTS } from '@/lib/form-field-editor-ports';
import { isModalClosed } from '@/lib/modalClosedRules';
import { parseTimeStamp, parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import { hotkeysList } from '@/lists/hotkeys';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { trackPatientRecapSourceSessionClicked } from '@/models/analytics/features/trackPatientRecapAnalytics';
import {
  trackWorkHomeComposerCta,
  trackWorkHomeComposerRoutineActioned,
  trackWorkHomeComposerViewed,
} from '@/models/analytics/features/trackWorkHomeComposerAnalytics';
import {
  resolveWorkBillingAnalyticsProps,
  trackMobileHandoffCardActioned,
  trackProjectChatErrorOnce,
  trackWorkArtifactCreated,
  trackWorkArtifactExported,
  trackWorkEvent,
  trackWorkExperienceFeedbackActioned,
  trackWorkUpgradeActioned,
  WorkLaunchEventName,
} from '@/models/analytics/features/work';
import { trackDefaultLandingPageBannerViewed } from '@/models/analytics/features/work-home-analytics';
import { useTrackWorkArtifactCanvasActions } from '@/models/analytics/features/workArtifactCanvas';
import {
  trackWorkflowActioned,
  trackWorkflowCreated,
  trackWorkflowInstalled,
} from '@/models/analytics/features/workflowAnalytics';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { store, type RootState } from '@/store';
import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import {
  cancelNativeDictation,
  shouldUseNativeAudio,
  stopNativeDictation,
} from '@/tauri/dictation';
import { readAudioLevels } from '@/tauri/events/audio-levels';
import {
  hasRpaErrorCode,
  listenRecordingExecutorStatus,
  listenRpaExecutorStatus,
  readComputerUseRefusal,
  RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE,
  rpaBridge,
  RpaBridgeError,
} from '@/tauri/rpa';
import { buildRecorderUploadMetadata } from '@/tauri/rpa-recorder/upload-auth-context';
import { coerceWorkflowWirePayload } from '@/tauri/rpa-recorder/upload-types';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { resolveExportDocumentFilename } from '@/utils/fileHelpers';
import { resolveFormTemplateUrl } from '@/utils/formTemplate';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import { getPdfPageDimensions } from '@/utils/pdf-fields';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { createLazyChunk } from '@/utils/retry-lazy';
import { reportBoundaryError } from '@/utils/telemetry/boundary-error';
import {
  configureWorkHomePorts,
  configureWorkHostPorts,
  useReferenceableLibraryDocuments,
  useVoiceAt,
  VoiceAssistantTrigger,
  VoiceWindow,
  type WorkHomePorts,
  type WorkHostPorts,
} from '@heidi/agents';
import type {
  TrackWorkDesktopNurtureBannerEvent,
  TrackWorkUpgradeActioned,
} from '@heidi/agents/contracts';
import {
  useAllWorkflowRuns,
  useResolvableWorkflow,
  useRoutineForProject,
  useRunAnyWorkflow,
  useSessionWorkflowRuns,
  useWorkflows,
} from '@heidi/agents/data';
import {
  getOptimizedBarHeights as getBarHeights,
  useAudioAnalyzer,
} from '@heidi/capture';
import {
  clearCreditAllowanceRefusal,
  creditAllowanceRefusalAtom,
  nudgeCreditAllowanceRefusal,
  rememberCreditAllowanceRefusal,
  shouldSkipCreditAllowanceFollowUpSend,
} from '@heidi/commerce';
import { SearchGroupTransition } from '@heidi/design-system/search-group-transition';
import { isEditorLive } from '@heidi/editor/core';
import { getDocumentType } from '@heidi/evidence/contracts';
import { useDocumentDownloadUrl } from '@heidi/evidence/data';
import { isGetMobileAppModalOpenAtom } from '@heidi/growth';
import {
  MergeConnectButton,
  NangoConnectButton,
  useConnectedAppsProvider,
} from '@heidi/integrations/connected-apps';
import { useLetter } from '@heidi/integrations/letters';
import { lettersQueryOptions, useQueryLetters } from '@heidi/integrations/letters/data';
import { track as trackViaPipeline } from '@heidi/runtime/analytics';
import { flattenSessionListPages, useQuerySessionsList } from '@heidi/session/data';
import { useHandleEditTemplate } from '@heidi/templates';
import { useWhenProviderReady } from '@openfeature/react-sdk';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import platform from 'platform';
import { createElement, lazy, useCallback, useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';

/** The landing picker, which is also what enables the desktop-target roster query. */
const useLandingDesktopTarget = () => useDesktopTargetSelection({ surface: 'landing' });

/** The boot-landing flag as a value + setter pair: the atom itself stays app-owned. */
const useHasBootLandedOnHome = (): [boolean, (next: boolean) => void] =>
  useAtom(hasBootLandedOnHomeAtom);

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMutateMarkModalAsViewedPort = () => useMutateMarkModalAsViewed().mutate;

/**
 * The two PostHog reads, each bound to its own key so the package never sees
 * one. `undefined` is a real answer — flags have not loaded — and is passed
 * through, because that is what the mount decision holds on.
 */
const useAgentHomeNavFlag = () => useFeatureFlagEnabled(AGENT_HOME_V1_FLAG_KEY);
const useObjectWorkspaceFlag = () =>
  useFeatureFlagEnabled(WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY);

const buildWorkHomePorts = (): WorkHomePorts => ({
  useOpenRoutineCreateChat,
  useLandingDesktopTarget,
  useDocumentDownloadUrl,
  pdf: { Document: ReactPdfDocument, Page: ReactPdfPage },
  getDocumentType,
  useIsDefaultLandingPageSettingEnabled,
  useDefaultLandingPageBannerWaitMs,
  useIsClinicalRecordingActive,
  useHasBootLandedOnHome,
  readBootPathname,
  isHomeBootPath,
  useQueryDefaultLandingPage,
  useMutateMarkModalAsViewed: useMutateMarkModalAsViewedPort,
  useSettingsModal,
  // Settings → Display controls, scrolled to and highlighting the Default page row.
  settingsHref: `/settings/display-controls${DEFAULT_LANDING_PAGE_SETTINGS_HASH}`,
  isModalClosed,
  parseTimeStampOrNull,
  trackButtonClicked,
  trackDefaultLandingPageBannerViewed,
  useAgentHomeNavFlag,
  useObjectWorkspaceFlag,
  usePosthogFlagsReady,
  useIsProjectsEnabled,
});

const isEscAlreadyClaimed = (event: KeyboardEvent) => event.defaultPrevented;

/** The page-wide Esc stop, registered in the app's hotkey list. */
const useStopGeneratingHotkey: WorkHostPorts['shell']['useStopGeneratingHotkey'] = (
  onStop,
  enabled
) => {
  useHotkeys(
    hotkeysList.stopGenerating.hotkeys,
    () => onStop(),
    {
      scopes: hotkeysList.stopGenerating.scopes,
      preventDefault: true,
      ignoreEventWhen: isEscAlreadyClaimed,
      enabled,
    },
    [onStop]
  );
};

const useAreShortcutsDisabled = () => useAtomValue(isShortcutsDisabledAtom);

const useNavigate = (): ReturnType<WorkHostPorts['shell']['useNavigate']> => {
  const router = useRouter();
  return useCallback(
    async (href) => {
      await router.push(href);
    },
    [router]
  );
};

const useRouteRead = () => {
  const router = useRouter();
  return { isReady: router.isReady, pathname: router.pathname, query: router.query };
};

const useReplaceRouteQuery = () => {
  const router = useRouter();
  return useCallback(
    (query: Record<string, string | string[]>) => {
      void router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      });
    },
    [router]
  );
};

const useTimerSeconds = () => useSelector((s: RootState) => s.timerModel.seconds);

/** Auth-token poke. The body is the store subscription the attach loop had inline. */
export const subscribeAuthTokenChange = (cb: () => void): (() => void) =>
  store.subscribe(cb);

/**
 * Device bootstrap the overlay ran before a desktop start/resume. No-op off
 * desktop; the marker fills a slot the native recorder has not chosen yet.
 */
export const ensureDesktopRecordingDevices = async (): Promise<void> => {
  if (!isTauri()) return;
  let recordState = store.getState().scribeRecordModel;
  if (!recordState.tauriInputDeviceSelected || !recordState.tauriOutputDeviceSelected) {
    await store.dispatch.scribeRecordModel.getTauriDevices({
      updateSelectedDevices: true,
    });
    recordState = store.getState().scribeRecordModel;
  }
  const systemDefaultDevice = {
    name: SYSTEM_DEFAULT_DEVICE_MARKER,
    is_default: true,
    display_name: null,
    icon_data_url: null,
    is_communications_default: false,
  };
  if (!recordState.tauriInputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriInputDevice(systemDefaultDevice);
  }
  if (!recordState.tauriOutputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriOutputDevice(systemDefaultDevice);
  }
};

const useSetIngestFieldCandidates = () => useSetAtom(ingestFieldCandidatesAtom);
const useSetIngestNotePushHints = () => useSetAtom(ingestNotePushHintsAtom);
const useSetIngestNotePushRecorded = () => useSetAtom(ingestNotePushRecordedAtom);
const useSetClearCandidateMappings = () => useSetAtom(clearCandidateMappingsAtom);

const useCopilotRunContextByProject = () => useAtomValue(copilotRunContextByProjectAtom);

const useCreditAllowanceRefusalValue = () => useAtomValue(creditAllowanceRefusalAtom);

const useSetGetMobileAppModalOpen = () => useSetAtom(isGetMobileAppModalOpenAtom);

const readEvidenceSession: WorkHostPorts['bridge']['readEvidenceSession'] = (stepId) =>
  getDefaultStore().get(evidenceSessionsAtom).get(stepId);

const subscribeEvidenceSessions: WorkHostPorts['bridge']['subscribeEvidenceSessions'] = (
  onChange
) => getDefaultStore().sub(evidenceSessionsAtom, onChange);

const readCopilotRunContextByProject = () =>
  getDefaultStore().get(copilotRunContextByProjectAtom);

const usePendingLetters = (sessionId: string) =>
  useAtomValue(pendingLettersAtomFamily(sessionId));

const useActiveSessionId = () => useAtomValue(activeSessionIdAtom);
const useSetActiveSessionId = () => useSetAtom(activeSessionIdAtom);
const useAgentRecordingSession = () => useAtom(agentRecordingSessionIdAtom);

// The overlay's pause and stop: the command boundary accepts each press, picks
// the recorder and tracks it, so the package sees none of that.
const useOverlayRecordingCommands: WorkHostPorts['session']['useRecordingCommands'] = (
  sessionId
) => {
  const recorder = useRecording();
  const { startRecording } = recorder;
  const commands = useRecordingControlCommands({
    sessionId: sessionId ?? undefined,
    recorder,
  });
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  return useMemo(() => {
    const outcome = async ({ accepted, settled }: RecordingControlResult) => {
      await settled;
      return { accepted };
    };
    return {
      startRecording: () => startRecording(),
      pause: () =>
        outcome(
          commands.pause({
            controlSurface: 'projects_overlay',
            beforeRecorder: () => setActiveSessionId(null),
          })
        ),
      stop: ({ fromPaused }) =>
        outcome(
          commands.stop({
            controlSurface: 'projects_overlay',
            fromPaused,
            beforeRecorder: () => setActiveSessionId(null),
          })
        ),
    };
  }, [commands, setActiveSessionId, startRecording]);
};

const useFormDraftTitle = () => useAtom(formDraftTitleAtom);
const useSetFormTemplateToEdit = () => useSetAtom(formTemplateToEditAtom);
const useSetFormFillingUploadOpen = () => useSetAtom(formFillingUploadModalOpenAtom);
const useIsFlagProviderReady = () => useWhenProviderReady({ suspendUntilReady: false });
const useCandidateMappings = () => useAtomValue(candidateMappingsAtom);
const useSetSectionOverride = () => useSetAtom(setSectionOverrideAtom);
const usePresentationAtom = () => useAtom(presentationAtom);
const useSetPresentationHistoryAtom = () => useSetAtom(presentationHistoryAtom);
const useSetPresentationOriginalAtom = () => useSetAtom(presentationOriginalAtom);
const useSetSelectedSlideIndexAtom = () => useSetAtom(selectedSlideIndexAtom);

const lazyNamed = (
  load: () => Promise<Record<string, unknown>>,
  exportName: string
): WorkHostPorts['session']['EmbeddingsPanel'] =>
  lazy(() =>
    load().then((mod) => ({
      default: mod[exportName] as WorkHostPorts['session']['EmbeddingsPanel'],
    }))
  );

const RecordingWorkflowSummary = lazyNamed(
  () => import('@/composition/hosts/agents/routines/RecordingWorkflowSummary'),
  'RecordingWorkflowSummary'
) as unknown as WorkHostPorts['routines']['RecordingWorkflowSummary'];

const EmbeddingsPanel = lazy(
  () => import('@/container/ScribeTabs/TabPane/EmbeddingsPanel')
) as unknown as WorkHostPorts['session']['EmbeddingsPanel'];
const RegulatoryGuidelineBlockModal = lazyNamed(
  () =>
    import('@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-block-modal'),
  'RegulatoryGuidelineBlockModal'
);
const RegulatoryGuidelineWarningModal = lazyNamed(
  () =>
    import('@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-warning-modal'),
  'RegulatoryGuidelineWarningModal'
);
const PatientDocumentsSection = lazyNamed(
  () => import('@heidi/patients'),
  'PatientDocumentsSection'
);
const DrawerSections = lazyNamed(() => import('@heidi/patients'), 'DrawerSections');
const SessionHistorySection = lazyNamed(
  () => import('@heidi/patients'),
  'SessionHistorySection'
);
const SlideEditorPanel = lazyNamed(
  () => import('@/components/slide-builder/SlideEditorPanel'),
  'SlideEditorPanel'
);
const SlideUndoRedoButtons = lazyNamed(
  () => import('@/components/slide-builder/SlideToolbar'),
  'SlideUndoRedoButtons'
);
const SessionArtifactModalHosts = lazyNamed(
  () => import('@/container/session-page/v2/main/SessionArtifactModalHosts'),
  'SessionArtifactModalHosts'
);
const LinkedSessionTabsHost = lazyNamed(
  () => import('@/container/session-panel/LinkedSessionTabsHost'),
  'LinkedSessionTabsHost'
);
const SplitScreenSkeletonLoader = lazy(
  () => import('@/container/session-panel/split-screen/skeleton-loader')
) as WorkHostPorts['session']['SplitScreenSkeletonLoader'];
const ClinicalTasksPanelHostProvider = lazyNamed(
  () =>
    import('@/container/session-page/v2/session-panel/tasks-card/clinical-tasks-panel-host'),
  'ClinicalTasksPanelHostProvider'
);
const TemplateMarkdownPreview = lazyNamed(
  () => import('@/components/template-markdown-preview'),
  'TemplateMarkdownPreview'
);
const ChunkErrorMessage = lazyNamed(
  () => import('@/components/ui/chunk-error-message'),
  'ChunkErrorMessage'
);
const RoutineOverviewPane = lazyNamed(
  () => import('@/features/workflows/components/RoutineOverviewPane'),
  'RoutineOverviewPane'
);
const RoutineCreateGuidedChat = lazyNamed(
  () => import('@/features/workflows/components/RoutineCreateGuidedChat'),
  'RoutineCreateGuidedChat'
) as unknown as WorkHostPorts['routines']['RoutineCreateGuidedChat'];

// These three render only inside the Work chat. Loading them here would put
// their modules on /overlay and /cua-status, which evaluate this wiring.
const ExperienceFeedbackNudge = lazyNamed(
  () => import('@/components/experience-feedback/ExperienceFeedbackNudge'),
  'ExperienceFeedbackNudge'
) as unknown as WorkHostPorts['shell']['ExperienceFeedbackNudge'];
const InSessionSourcesButton = lazyNamed(
  () => import('@/components/ui/floating-ask-heidi/floating-ask-heidi-input'),
  'InSessionSourcesButton'
) as unknown as WorkHostPorts['evidence']['InSessionSourcesButton'];
const RoutineRunChatHeader = lazyNamed(
  () => import('@/features/workflows/components/RoutineRunChatHeader'),
  'RoutineRunChatHeader'
) as unknown as WorkHostPorts['routines']['RoutineRunChatHeader'];

// A static import of these four is on /overlay and /cua-status, which evaluate
// this wiring. Bind them the same way as RoutineRunChatHeader.
const ChatObjectList = lazyNamed(
  () => import('@/composition/hosts/chat-object-list'),
  'ChatObjectList'
) as unknown as WorkHostPorts['evidence']['ChatObjectList'];
const CloudComputerInlineLiveView = lazyNamed(
  () => import('@/features/rpa/cua-cloud/CloudComputerInlineLiveView'),
  'CloudComputerInlineLiveView'
) as unknown as WorkHostPorts['desktop']['CloudComputerInlineLiveView'];
const DesktopControlApprovalWarning = lazyNamed(
  () => import('@/features/rpa/desktop-control-approval-warning'),
  'DesktopControlApprovalWarning'
) as unknown as WorkHostPorts['desktop']['DesktopControlApprovalWarning'];
const CreateWorkflowApprovalCard = lazyNamed(
  () => import('@/features/workflows/components/CreateWorkflowApprovalCard'),
  'CreateWorkflowApprovalCard'
) as unknown as WorkHostPorts['routines']['CreateWorkflowApprovalCard'];

const LinkedContextBadge = lazyNamed(
  () => import('@/components/ui/linked-context-badge'),
  'LinkedContextBadge'
);

const loadV2FocusedSessionArtifactPanel = () =>
  import('@/container/session-page/v2/main/v2-focused-session-artifact-panel').then(
    (mod) => ({
      default:
        mod.V2FocusedSessionArtifactPanel as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
    })
  );
const loadSessionPreviewArtifact = () =>
  import('@/container/session-page/v2/main/SessionPreviewArtifact').then((mod) => ({
    default:
      mod.SessionPreviewArtifact as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadShareTemplateModal = () =>
  import('@/components/modal/share-template-modal').then((mod) => ({
    default:
      mod.ShareTemplateModal as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadLetterTabPane = () =>
  import('@/composition/hosts/integrations/letter-sender-v2/components/letter-tab-pane').then(
    (mod) => ({
      default:
        mod.LetterTabPane as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
    })
  );
// projects components — bound by reference, called where the moved modules called them.
// The workflows and computer-use UI below loads on first use, never in a route's first load.
const CloudComputerExpandButton = lazyNamed(
  () => import('@/features/rpa/cua-cloud/CloudComputerLightbox'),
  'CloudComputerExpandButton'
);
const CloudComputerLightbox = lazyNamed(
  () => import('@/features/rpa/cua-cloud/CloudComputerLightbox'),
  'CloudComputerLightbox'
);
const RoutineRecentRunsRail = lazyNamed(
  () => import('@/features/workflows/components/RoutineRecentRunsRail'),
  'RoutineRecentRunsRail'
);
const loadPresetSetupDialog = () =>
  import('@/features/workflows/hooks/usePresetSetupDialog');
const loadWorkflowShortcutListeners = () =>
  import('@/features/workflows/useWorkflowShortcutListeners');
const CloudComputerLiveView = lazy(() =>
  import('@/features/rpa/cua-cloud/CloudComputerLiveView').then((mod) => ({
    default: mod.CloudComputerLiveView,
  }))
);
const RoutineDockCarousel = lazy(() =>
  import('@/features/workflows/components/RoutineDockCarousel').then((mod) => ({
    default: mod.RoutineDockCarousel,
  }))
);
const WorkflowsSplitView = lazy(() =>
  import('@/features/workflows/components/WorkflowsSplitView').then((mod) => ({
    default: mod.WorkflowsSplitView,
  }))
);
// projects PR4a — Chat-bound components, one lazy boundary each, never per mount.
const EvidenceTimelineBody = lazy(() =>
  import('@/features/projects/components/EvidenceTimelineBody').then((mod) => ({
    default: mod.EvidenceTimelineBody,
  }))
);
const WorkSourcesPopover = lazy(() =>
  import('@/features/projects/components/LinkContextButton.parts').then((mod) => ({
    default: mod.WorkSourcesPopover,
  }))
);
const ComposerShortcutMarketplace = lazy(() =>
  import('@/components/ui/floating-ask-heidi/composer-shortcut-marketplace').then(
    (mod) => ({
      default: mod.ComposerShortcutMarketplace,
    })
  )
);

const useReadyToRequestMicPermissions = () =>
  useAtomValue(readyToRequestMicPermissionsAtom);
const useSetEvidenceDisclaimerModalOpen = () =>
  useSetAtom(evidenceDisclaimerModalOpenAtom);
const useAnyDialogDrawerOpen = () => useAtomValue(anyDialogDrawerOpenAtom);

const isMacOS = (): boolean => {
  const family = platform.os?.family?.toLowerCase();
  return family?.includes('mac') === true || platform.os?.family === 'OS X';
};

const useRoutinesDesktopNurtureBannerDismissed = (): [boolean, (next: boolean) => void] =>
  useAtom(routinesDesktopNurtureBannerDismissedAtom);

const trackNurtureEvent: TrackWorkDesktopNurtureBannerEvent = (eventName, props) => {
  trackViaPipeline({
    event_name: eventName,
    props: {
      surface: 'work_home',
      region: getRegion(),
      ...props,
    },
    options: { amplitude: false, posthog: false },
  });
};

const trackUpgradeActioned: TrackWorkUpgradeActioned = (props) =>
  trackWorkUpgradeActioned({ ...props, ...resolveWorkBillingAnalyticsProps() });

const useRunRoutineToChatPort = (): ((workflowId: string) => void) => {
  const run = useRunRoutineToChat();
  return (workflowId: string) => {
    run(workflowId);
  };
};

// The page is read above the override, so a session page keeps its own session:
// the same precedence `useRecordingSessionId` applies.
const RecorderSessionScope: WorkHostPorts['session']['RecorderSessionScope'] = ({
  sessionId,
  children,
}) => {
  const pageSessionId = useCurrentSessionId();
  return createElement(
    SessionIdOverrideContext.Provider,
    { value: pageSessionId ? null : sessionId },
    children
  );
};

const buildWorkHostBundles = (workHome: WorkHomePorts): WorkHostPorts => ({
  ...workHome,
  desktop: {
    emitProjectAgentRegistryPoke,
    listenProjectAgentRegistryPoke,
    useUserWorkflows: useUserWorkflows as WorkHostPorts['desktop']['useUserWorkflows'],
    deriveAvailableWorkflowSkillsFromSummaries:
      deriveAvailableWorkflowSkillsFromSummaries as unknown as WorkHostPorts['desktop']['deriveAvailableWorkflowSkillsFromSummaries'],
    rpaBridge,
    useSetIngestFieldCandidates:
      useSetIngestFieldCandidates as WorkHostPorts['desktop']['useSetIngestFieldCandidates'],
    useSetIngestNotePushHints:
      useSetIngestNotePushHints as WorkHostPorts['desktop']['useSetIngestNotePushHints'],
    useSetIngestNotePushRecorded:
      useSetIngestNotePushRecorded as WorkHostPorts['desktop']['useSetIngestNotePushRecorded'],
    useSetClearCandidateMappings,
    notePushTargetsQueryKey: NOTE_PUSH_TARGETS_QUERY_KEY,
    cancelComputerUseRun,
    useIsExecutorStopSignalled: useExecutorStopSignal,
    getAuthToken,
    isThisComputerTarget,
    useNativeInstanceId,
    useDesktopTargetRoster,
    getDomains,
    listenRecordingExecutorStatus:
      listenRecordingExecutorStatus as WorkHostPorts['desktop']['listenRecordingExecutorStatus'],
    listenRpaExecutorStatus:
      listenRpaExecutorStatus as WorkHostPorts['desktop']['listenRpaExecutorStatus'],
    buildRecorderUploadMetadata,
    useCopilotRunContextByProject,
    readCopilotRunContextByProject,
    releaseDesktopControlApprovalReservation,
    releaseDesktopControlApprovalReservationsForAgentRun,
    subscribeDesktopControlLease,
    useDesktopPermissionGate,
    hasRpaErrorCode,
    readComputerUseRefusal,
    RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE,
    RpaBridgeError,
    deriveWorkflowDetailPresentation,
    parseWorkflow,
    coerceWorkflowWirePayload,
    DesktopBars,
    SYSTEM_DEFAULT_DEVICE_MARKER,
    // projects artifacts
    candidateMappingKey,
    useCandidateMappings,
    useSetSectionOverride:
      useSetSectionOverride as WorkHostPorts['desktop']['useSetSectionOverride'],
    useQueryComputerUseNotePushTargets:
      useQueryComputerUseNotePushTargets as WorkHostPorts['desktop']['useQueryComputerUseNotePushTargets'],
    // projects components
    ParamChip: ParamChip as unknown as WorkHostPorts['desktop']['ParamChip'],
    TemplateParts: TemplateParts as unknown as WorkHostPorts['desktop']['TemplateParts'],
    humanizeParameterName,
    CloudComputerExpandButton:
      CloudComputerExpandButton as unknown as WorkHostPorts['desktop']['CloudComputerExpandButton'],
    CloudComputerLightbox: CloudComputerLightbox,
    CloudComputerLiveView: CloudComputerLiveView,
    useOverlayProjectCreatedListener,
    invalidateShellCachesOnEvent,
    useProjectFileExecutorAttach: useProjectFileExecutorAttach,
    useRoutinesDesktopNurtureBannerDismissed,
    routinesDesktopNurtureBannerCountThreshold:
      ROUTINES_DESKTOP_NURTURE_BANNER_COUNT_THRESHOLD,
    useIsWorkDesktopNurtureBannerEnabled,
    OverlayShimmerText,
    isTauri,
    // projects PR4a
    useReadyToRequestMicPermissions,
    readTauriAudioLevels: readAudioLevels,
    getBarHeights,
    useAudioAnalyzer,
    isValidDesktopInstanceId,
    readNativeInstanceId,
    resolveDesktopTargetTurn:
      resolveDesktopTargetTurn as unknown as WorkHostPorts['desktop']['resolveDesktopTargetTurn'],
    useDesktopTargetSelection: useDesktopTargetSelection,
    useAudioLevelsSubscription,
    cancelNativeDictation,
    shouldUseNativeAudio,
    stopNativeDictation,
    // projects PR4b
    classifyCuaCloudProviderState,
    CUA_CLOUD_MACHINE_QUERY_KEY,
    cuaCloudClient: cuaCloudClient,
    desktopControlApprovalPort,
    CloudComputerInlineLiveView: CloudComputerInlineLiveView,
    DesktopControlApprovalWarning: DesktopControlApprovalWarning,
    DESKTOP_TARGETS_QUERY_KEY,
    useComputerUseQueuedTarget: useComputerUseQueuedTarget,
    ProtectedActionApproval:
      ProtectedActionApproval as unknown as WorkHostPorts['desktop']['ProtectedActionApproval'],
    useIsDesktopOnlineReady,
  },
  session: {
    useOfflinePendingServerDeleteIds,
    flattenSessionListPages,
    useQuerySessionsList,
    useQueryPatientProfiles,
    useQuerySessionsV2,
    usePrefetchSessionById,
    useSessionWindow: useSessionWindow as WorkHostPorts['session']['useSessionWindow'],
    useSessionWindows,
    useQuerySessionWindows,
    useActiveSessionId,
    useSetActiveSessionId,
    useAgentRecordingSession,
    useMutateUpdateSessionRecordingStatus,
    useRecordingCommands: useOverlayRecordingCommands,
    useTimerSeconds,
    ensureDesktopRecordingDevices,
    RecorderSessionScope,
    // projects artifacts
    SessionIdOverrideContext,
    EmbeddingsPanel,
    useFormDraftTitle: useFormDraftTitle as WorkHostPorts['session']['useFormDraftTitle'],
    useSetFormTemplateToEdit:
      useSetFormTemplateToEdit as WorkHostPorts['session']['useSetFormTemplateToEdit'],
    useSaveFormTemplate:
      useSaveFormTemplate as WorkHostPorts['session']['useSaveFormTemplate'],
    FormViewType,
    RegulatoryGuidelineBlockModal,
    RegulatoryGuidelineWarningModal,
    useFormFieldEditorFlags: useFormFieldEditorFlags,
    useQueryTemplateById:
      useQueryTemplateById as WorkHostPorts['session']['useQueryTemplateById'],
    useFormDraftTitleState:
      useFormDraftTitleState as WorkHostPorts['session']['useFormDraftTitleState'],
    FORM_FIELD_EDITOR_PORTS,
    resolveFormTemplateUrl:
      resolveFormTemplateUrl as unknown as WorkHostPorts['session']['resolveFormTemplateUrl'],
    getPdfPageDimensions:
      getPdfPageDimensions as WorkHostPorts['session']['getPdfPageDimensions'],
    PatientDocumentsSection,
    DrawerSections,
    SessionHistorySection,
    useOpenPatientFullPage,
    useMutateUpdatePatientProfile:
      useMutateUpdatePatientProfile as unknown as WorkHostPorts['session']['useMutateUpdatePatientProfile'],
    usePresentationAtom:
      usePresentationAtom as WorkHostPorts['session']['usePresentationAtom'],
    useSetPresentationHistoryAtom:
      useSetPresentationHistoryAtom as WorkHostPorts['session']['useSetPresentationHistoryAtom'],
    useSetPresentationOriginalAtom:
      useSetPresentationOriginalAtom as WorkHostPorts['session']['useSetPresentationOriginalAtom'],
    useSetSelectedSlideIndexAtom:
      useSetSelectedSlideIndexAtom as WorkHostPorts['session']['useSetSelectedSlideIndexAtom'],
    usePresentation: usePresentation,
    slideBuilderMessages: slideBuilderMessages,
    SlideEditorPanel,
    SlideUndoRedoButtons,
    useSlideToolbarActions: useSlideToolbarActions,
    useSlideUndoRedoHotkeys: useSlideUndoRedoHotkeys,
    sessionWindowsAtomFamily,
    SessionArtifactModalHosts,
    LinkedSessionTabsHost,
    loadSplitScreen,
    reportSplitScreenPreloadFailure,
    focusedArtifactTab:
      focusedArtifactTab as WorkHostPorts['session']['focusedArtifactTab'],
    toFocusedArtifactTarget:
      toFocusedArtifactTarget as WorkHostPorts['session']['toFocusedArtifactTarget'],
    SplitScreenSkeletonLoader,
    useOpenSessionPage:
      useOpenSessionPage as WorkHostPorts['session']['useOpenSessionPage'],
    loadV2FocusedSessionArtifactPanel,
    loadSessionPreviewArtifact,
    ClinicalTasksPanelHostProvider,
    TemplateMarkdownPreview,
    useHandleEditTemplate: () => useHandleEditTemplate(handleEditTemplateHost),
    writeHtmlAndMarkdownToClipboard:
      writeHtmlAndMarkdownToClipboard as unknown as WorkHostPorts['session']['writeHtmlAndMarkdownToClipboard'],
    describeClipboardError,
    useQueryDocumentTemplates: useQueryDocumentTemplates,
    loadShareTemplateModal,
    GET_SESSIONS_LIST,
    useQueryDocumentsBySessionId: useQueryDocumentsBySessionId,
    usePatientProfileHost: usePatientProfileHost,
    // projects PR4a
    handleSlideDeleteChunk: handleSlideDeleteChunk,
    handleSlideUpdateChunk: handleSlideUpdateChunk,
    isEditorLive: isEditorLive as WorkHostPorts['session']['isEditorLive'],
    TabType,
    useDictationStore: useDictationStore,
    // projects PR4b
    DraftNoteBody: DraftNoteBody,
    useTemplateCreationApprovalGuard: useTemplateCreationApprovalGuard,
    usePendingWorkFirstTurnHandoff:
      usePendingWorkFirstTurnHandoff as unknown as WorkHostPorts['session']['usePendingWorkFirstTurnHandoff'],
    useSessionWorkLandingResearch: useSessionWorkLandingResearch,
    useSessionWorkLandingViewModel: useSessionWorkLandingViewModel,
  },
  evidence: {
    getResolvedShortcutsForMarket,
    buildSearchableShortcuts,
    useEvidenceChipsConfig,
    patientSexAge,
    cancelChatAttachmentUpload,
    useMutateDeleteChatAttachment,
    useMutateUploadChatAttachment,
    useChatFileUpload:
      useChatFileUpload as WorkHostPorts['evidence']['useChatFileUpload'],
    formatChatUploadErrorMessage,
    useEvidenceIfuHref,
    useEvidenceChatSurfaceGate,
    // projects artifacts
    LinkedContextBadge,
    // projects components
    SearchGroupTransition:
      SearchGroupTransition as unknown as WorkHostPorts['evidence']['SearchGroupTransition'],
    ChatObjectReference:
      ChatObjectReference as unknown as WorkHostPorts['evidence']['ChatObjectReference'],
    RefMarkdownElement: RefMarkdownElement,
    resolveExportDocumentFilename,
    // projects PR4a
    useSetEvidenceDisclaimerModalOpen: useSetEvidenceDisclaimerModalOpen,
    ImageFileCard: ImageFileCard as unknown as WorkHostPorts['evidence']['ImageFileCard'],
    ComposerShortcutMarketplace:
      ComposerShortcutMarketplace as unknown as WorkHostPorts['evidence']['ComposerShortcutMarketplace'],
    filterShortcuts: filterShortcuts,
    useComposerShortcutsFlyout: useComposerShortcutsFlyout,
    ChatObjectTokenHoverCard:
      ChatObjectTokenHoverCard as unknown as WorkHostPorts['evidence']['ChatObjectTokenHoverCard'],
    useCachedReferenceLabel:
      useCachedReferenceLabel as WorkHostPorts['evidence']['useCachedReferenceLabel'],
    EvidenceAnswerPortsHost:
      EvidenceAnswerPortsHost as unknown as WorkHostPorts['evidence']['EvidenceAnswerPortsHost'],
    ChatMessageContext:
      ChatMessageContext as unknown as WorkHostPorts['evidence']['ChatMessageContext'],
    EvidenceRegulatoryFootnoteWithIfu:
      EvidenceRegulatoryFootnoteWithIfu as unknown as WorkHostPorts['evidence']['EvidenceRegulatoryFootnoteWithIfu'],
    useQueryEvidenceIntegrations: useQueryEvidenceIntegrations,
    getPreviewUrl: getPreviewUrl,
    useChatPaste: useChatPaste,
    useEvidenceAccess: useEvidenceAccess,
    // projects PR4b
    useEvidenceSourceOpeners:
      useEvidenceSourceOpeners as WorkHostPorts['evidence']['useEvidenceSourceOpeners'],
    InSessionSourcesButton: InSessionSourcesButton,
    usePersistedWorkMode,
    useStartEvidenceChat,
    SessionReferenceOpenContext,
    ChatObjectList: ChatObjectList,
  },
  integrations: {
    usePendingLetters,
    // projects artifacts
    useLetter: useLetter as WorkHostPorts['integrations']['useLetter'],
    useQueryLetters: useQueryLetters,
    useLetterSenderActions: useLetterSenderActions,
    loadLetterTabPane,
    // projects components
    useConnectedAppsProvider: useConnectedAppsProvider,
    MergeConnectButton,
    NangoConnectButton,
    getEhrProviderMetadata: getEhrProviderMetadata,
    // projects PR4a
    lettersQueryOptions:
      lettersQueryOptions as unknown as WorkHostPorts['integrations']['lettersQueryOptions'],
  },
  routines: {
    useWorkflows,
    useReferenceableLibraryDocuments,
    // projects artifacts
    useVoiceAt: useVoiceAt as WorkHostPorts['routines']['useVoiceAt'],
    VoiceWindow: VoiceWindow as unknown as WorkHostPorts['routines']['VoiceWindow'],
    RoutineOverviewPane,
    useResolvableWorkflow,
    // projects components
    RoutineRecentRunsRail:
      RoutineRecentRunsRail as unknown as WorkHostPorts['routines']['RoutineRecentRunsRail'],
    useRoutineForProject: useRoutineForProject,
    useRecentRunsRail,
    useAllWorkflowRuns: useAllWorkflowRuns,
    useSessionWorkflowRuns: useSessionWorkflowRuns,
    loadPresetSetupDialog,
    RoutineDockCarousel:
      RoutineDockCarousel as unknown as WorkHostPorts['routines']['RoutineDockCarousel'],
    WorkflowsSplitView: WorkflowsSplitView,
    useRunRoutineToChat: useRunRoutineToChatPort,
    loadWorkflowShortcutListeners,
    // projects PR4a
    VoiceAssistantTrigger:
      VoiceAssistantTrigger as unknown as WorkHostPorts['routines']['VoiceAssistantTrigger'],
    useRunAnyWorkflow:
      useRunAnyWorkflow as unknown as WorkHostPorts['routines']['useRunAnyWorkflow'],
    // projects PR4b
    RoutineRunChatHeader: RoutineRunChatHeader,
    CreateWorkflowApprovalCard: CreateWorkflowApprovalCard,
    RoutineCreateGuidedChat,
    useSetFormFillingUploadOpen,
    detectShortcutConflict,
    analytics: { trackWorkflowActioned, trackWorkflowInstalled, trackWorkflowCreated },
    RecordingWorkflowSummary,
    WorkflowEditorBoldButton: BoldButton,
    WorkflowEditorItalicButton: ItalicButton,
    WorkflowEditorBulletListButton: BulletListButton,
  },
  shell: {
    useIsRpaRecorderEnabled,
    useIsWorkflowsEnabled,
    useIsAskHeidiSourcesShortcutsEnabled,
    useIsChatObjectRefsEnabled,
    isSlideBuilderEnabled: IS_SLIDE_BUILDER_ENABLED,
    isChatAttachmentsEnabled: IS_CHAT_ATTACHMENTS_ENABLED,
    parseTimeStamp,
    isLogoutInFlight,
    useAreShortcutsDisabled,
    creditAllowanceRefusal: {
      remember: rememberCreditAllowanceRefusal,
      clear: clearCreditAllowanceRefusal,
      nudge: nudgeCreditAllowanceRefusal,
      shouldSkipFollowUpSend: shouldSkipCreditAllowanceFollowUpSend,
      useValue: useCreditAllowanceRefusalValue,
    },
    useStopGeneratingHotkey,
    resolveWorkBillingAnalyticsProps,
    trackWorkEvent,
    workLaunchEventName: WorkLaunchEventName,
    subscribeAuthTokenChange,
    useNavigate,
    useRouteRead,
    useReplaceRouteQuery,
    useQueryKindeProperties,
    // projects artifacts
    IS_INSET_DESIGN_ENABLED,
    useIsAgentHomeV1Enabled,
    useIsPatientListEnabled,
    useTrackWorkArtifactCanvasActions,
    trackWorkArtifactCreated,
    trackWorkArtifactExported,
    trackPatientRecapSourceSessionClicked:
      trackPatientRecapSourceSessionClicked as WorkHostPorts['shell']['trackPatientRecapSourceSessionClicked'],
    cn: cn as WorkHostPorts['shell']['cn'],
    reportBoundaryError:
      reportBoundaryError as WorkHostPorts['shell']['reportBoundaryError'],
    ChunkErrorMessage,
    createLazyChunk:
      createLazyChunk as unknown as WorkHostPorts['shell']['createLazyChunk'],
    // projects components
    useIsCountrySpecificEhrSelectionEnabled,
    personalizationSharedMessages: personalizationSharedMessages,
    useUserCountryCode,
    isProductFeedbackAvailable,
    getRegion: getRegion,
    useQueryUser: useQueryUser,
    trackNurtureEvent,
    trackUpgradeActioned,
    useIsNangoIntegrationsEnabled,
    useWhiteLabelSafe,
    useIsCmdShiftHOverlayEnabled,
    isMacOS,
    OpenTemplateEditorBlock:
      OpenTemplateEditorBlock as unknown as WorkHostPorts['shell']['OpenTemplateEditorBlock'],
    TemplatePinBlock:
      TemplatePinBlock as unknown as WorkHostPorts['shell']['TemplatePinBlock'],
    WorkArtefactCreatedCard:
      WorkArtefactCreatedCard as unknown as WorkHostPorts['shell']['WorkArtefactCreatedCard'],
    useIsWorkSourceCitationChipsEnabled,
    reactQueryClient,
    // projects PR4a
    useRouteInterception:
      useRouter as unknown as WorkHostPorts['shell']['useRouteInterception'],
    cancelNavMenuSwitchTracking,
    ComposerDictateSlot:
      ComposerDictateSlot as unknown as WorkHostPorts['shell']['ComposerDictateSlot'],
    renderPlaceholderSlots:
      renderPlaceholderSlots as WorkHostPorts['shell']['renderPlaceholderSlots'],
    useAnyDialogDrawerOpen,
    useIsEvidenceInlineImagesEnabled,
    useIsEvidenceSubagentResidualCapabilitiesEnabled,
    useIsWorkEvidenceDisclaimerEnabled,
    useIsWorkSessionArtifactV2Enabled,
    useIsUKOrEU,
    useMediaQuery,
    getPlatform,
    track: track,
    evidenceShortcutRunEventName: TrackingEventName.EvidenceShortcutRun,
    trackWorkHomeComposerCta,
    trackWorkHomeComposerRoutineActioned,
    trackWorkHomeComposerViewed,
    trackProjectChatErrorOnce: trackProjectChatErrorOnce,
    isOnPrem,
    isDevelopmentBuild: process.env.NODE_ENV === 'development',
    // projects PR4b
    ExperienceFeedbackNudge: ExperienceFeedbackNudge,
    mergeCreditAllowanceGhost: mergeCreditAllowanceGhost,
    useIsProjectsContextualLoaderPhrasesEnabled,
    useIsWorkLetterArtifactEnabled,
    useIsDesktopTargetSelectorEnabled,
    useIsEntitlementEnabled: useIsEntitlementEnabled,
    trackMobileHandoffCardActioned: trackMobileHandoffCardActioned,
    trackWorkExperienceFeedbackActioned,
    useSetGetMobileAppModalOpen,
    mobileAppHeaderMessages,
    useIsWorkInSessionEnabled,
    useIsFlagProviderReady,
    DEFAULT_DATE_FORMAT,
  },
  bridge: {
    EvidenceTimelineBody:
      EvidenceTimelineBody as unknown as WorkHostPorts['bridge']['EvidenceTimelineBody'],
    useProjectEvidenceSession: useProjectEvidenceSession,
    buildEvidenceSessionFromPayload: buildEvidenceSessionFromPayload,
    readEvidenceRenderPayload: readEvidenceRenderPayload,
    WorkSourcesPopover:
      WorkSourcesPopover as unknown as WorkHostPorts['bridge']['WorkSourcesPopover'],
    stripCiteTags,
    stripImgTags,
    buildCiteMarkers,
    buildImageMarkers,
    buildReferences: buildReferences,
    buildCitationRegistry,
    buildImageRegistry,
    parseEvidenceAgentSearchPayload,
    toChatSource: toChatSource,
    useEvidenceAnswerComponents: useEvidenceAnswerComponents,
    renderCreateTemplateToolUse:
      renderCreateTemplateToolUse as WorkHostPorts['bridge']['renderCreateTemplateToolUse'],
    renderUpdateWordLibraryToolUse:
      renderUpdateWordLibraryToolUse as WorkHostPorts['bridge']['renderUpdateWordLibraryToolUse'],
    handleEvidenceChunkStreamEvent: handleEvidenceChunkStreamEvent,
    // projects PR4b
    readEvidenceSession,
    subscribeEvidenceSessions,
  },
});

export interface AppPorts {
  workHome: WorkHomePorts;
  workHost: WorkHostPorts;
}

/** Builds the Work port bundles. Runs on the package's first read, never at module scope. */
export const createAppPorts = (): AppPorts => {
  const workHome = buildWorkHomePorts();
  return { workHome, workHost: buildWorkHostBundles(workHome) };
};

let built: AppPorts | undefined;
const appPorts = (): AppPorts => (built ??= createAppPorts());

configureWorkHomePorts(() => appPorts().workHome);
configureWorkHostPorts(() => appPorts().workHost);
